import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  FormControl,
  Select,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClearIcon from '@mui/icons-material/Clear';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { rejectInvoice } from './functions/start_stop_invoice.function';
import { notify } from '../../common/functions/react-toastify.function';

export const InvoiceTable = ({ poNumber, invoices = [], poDetails = null, fromTab = null }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(null);
  const [invoiceDateTo, setInvoiceDateTo] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);

  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    invoiceId: null,
    invoiceNumber: '',
    reason: '',
  });
  const [rejectLoading, setRejectLoading] = useState(false);

  const clearInvoiceDates = () => {
    setInvoiceDateFrom(null);
    setInvoiceDateTo(null);
  };

  // Assignee filter uses Current Approver values
  const assignees = useMemo(() => {
    const uniqueAssignees = [
      ...new Set(invoices.map((item) => item.currentApprover).filter(Boolean)),
    ];
    return uniqueAssignees.sort();
  }, [invoices]);

  const vendors = useMemo(() => {
    const uniqueVendors = [...new Set(invoices.map((item) => item.vendor).filter(Boolean))];
    return uniqueVendors.sort();
  }, [invoices]);

  const regions = useMemo(() => {
    const uniqueRegions = [...new Set(invoices.map((item) => item.region).filter(Boolean))];
    return uniqueRegions.sort();
  }, [invoices]);

  const statuses = useMemo(() => {
    const uniqueStatuses = [
      ...new Set(invoices.map((item) => item.approvalStatus).filter(Boolean)),
    ];
    return uniqueStatuses.sort();
  }, [invoices]);

  const isPendingRejectionRow = (rowLike) => {
    const raw = (rowLike?.statusRaw || '').toLowerCase().trim();
    if (raw === 'pending_rejection') {
      return true;
    }
    const display = (rowLike?.approvalStatus || '').toLowerCase().trim();
    return display === 'rejection pending';
  };

  // Open invoice details (used by both invoice number click and three-dots menu)
  const handleOpenInvoiceDetails = useCallback(
    (rowData) => {
      const baseInvoice = rowData._originalData || rowData;
      const invoiceData = {
        ...baseInvoice,
        assigned_role:
          baseInvoice.assigned_role || rowData.assigned_role || rowData.assignedRole || null,
        currentApprover: rowData.currentApprover || baseInvoice.currentApprover || '',
      };
      navigate('/poWorkflowManagement/invoices/details', {
        state: {
          invoiceData,
          poNumber,
          invoices,
          poDetails,
          fromTab,
        },
      });
    },
    [navigate, poNumber, invoices, poDetails, fromTab]
  );

  // Actions Menu Component
  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const isPendingRejection = isPendingRejectionRow(row.original);

    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleOpenDetails = () => {
      handleClose();
      handleOpenInvoiceDetails(row.original);
    };

    const handleReject = () => {
      handleClose();
      const inv = row.original?._originalData || {};
      const invNo = row.original?.invoiceNumber || inv.invoice_number || '';
      const invId = inv.id ?? row.original?.id ?? null;
      const existingReason =
        row.original?.reasonOfRejection ||
        inv.reason_of_rejection ||
        row.original?.reasonOfRejection ||
        '';
      setRejectDialog({
        open: true,
        invoiceId: invId,
        invoiceNumber: invNo,
        reason: (existingReason || '').toString(),
      });
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            color: '#667085',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={handleOpenDetails}
            sx={{
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemText primary="Open Details" />
          </MenuItem>
          <MenuItem
            onClick={handleReject}
            disabled={!isPendingRejection}
            sx={{
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemText primary="Reject" />
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        size: 200,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const invoiceNumber = cell.getValue();
          const rowData = cell.row.original;
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenInvoiceDetails(rowData);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenInvoiceDetails(rowData);
                }
              }}
              style={{
                fontSize: '14px',
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1565c0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1976d2';
              }}
            >
              {invoiceNumber || 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Invoice Date',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const date = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'invoiceAmount',
        header: 'Invoice Amount',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const amount = cell.getValue();
          return (
            <span style={{ fontSize: '14px', color: '#667085' }}>
              {amount ? `PKR ${amount?.toLocaleString()}` : 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: 'reasonOfRejection',
        header: 'Reason',
        size: 220,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const reason = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{reason || '-'}</span>;
        },
      },
      {
        accessorKey: 'milestone',
        header: 'Milestone',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const milestone = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{milestone || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const status = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{status || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'currentApprover',
        header: 'Current Approver',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const approver = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{approver || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'previousApprover',
        header: 'Previous Approver',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const approver = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{approver || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const city = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{city || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'region',
        header: 'Region',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const region = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{region || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const vendor = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{vendor || 'N/A'}</span>;
        },
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <ActionsMenu row={row} />,
      },
    ],
    [handleOpenInvoiceDetails]
  );

  // Filter data based on search term, filters
  const filteredData = useMemo(() => {
    let filtered = invoices;

    // Filter by Invoice Date (date-only comparison)
    if (invoiceDateFrom || invoiceDateTo) {
      filtered = filtered.filter((item) => {
        if (!item.invoiceDate) {
          return false;
        }
        const itemDateStr = dayjs(item.invoiceDate).format('YYYY-MM-DD');
        if (invoiceDateFrom) {
          const fromStr = dayjs(invoiceDateFrom).format('YYYY-MM-DD');
          if (itemDateStr < fromStr) {
            return false;
          }
        }
        if (invoiceDateTo) {
          const toStr = dayjs(invoiceDateTo).format('YYYY-MM-DD');
          if (itemDateStr > toStr) {
            return false;
          }
        }
        return true;
      });
    }

    // Filter by assignee (using currentApprover field)
    if (selectedAssignee) {
      filtered = filtered.filter((item) => item.currentApprover === selectedAssignee);
    }

    // Filter by vendor
    if (selectedVendor) {
      filtered = filtered.filter((item) => item.vendor === selectedVendor);
    }

    // Filter by region
    if (selectedRegion) {
      filtered = filtered.filter((item) => item.region === selectedRegion);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((item) => item.approvalStatus === selectedStatus);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  }, [
    searchTerm,
    invoiceDateFrom,
    invoiceDateTo,
    selectedAssignee,
    selectedVendor,
    selectedRegion,
    selectedStatus,
    invoices,
  ]);

  // Reset pagination to first page when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    invoiceDateFrom,
    invoiceDateTo,
    selectedAssignee,
    selectedVendor,
    selectedRegion,
    selectedStatus,
  ]);

  // Download Excel function
  const handleDownloadExcel = (tableInstance) => {
    // Get filtered rows from the table instance (includes all filters: custom + MUI column filters)
    // If table instance is provided, use filtered rows; otherwise fallback to filteredData
    let dataToDownload = filteredData;

    if (tableInstance) {
      const filteredRows = tableInstance.getFilteredRowModel().rows;
      dataToDownload = filteredRows.map((row) => row.original);
    }

    if (dataToDownload.length === 0) {
      return;
    }

    try {
      const excelData = dataToDownload.map((item) => ({
        'Invoice Number': item.invoiceNumber || '',
        'Invoice Date': item.invoiceDate || '',
        'Invoice Amount': item.invoiceAmount || '',
        Milestone: item.milestone || '',
        'Approval Status': item.approvalStatus || '',
        'Current Approver': item.currentApprover || '',
        'Previous Approver': item.previousApprover || '',
        City: item.city || '',
        Region: item.region || '',
        Vendor: item.vendor || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Invoices_${poNumber}_${currentDate}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: Array.isArray(filteredData) ? filteredData : [],
    state: {
      isLoading: loading,
      pagination,
    },
    onPaginationChange: setPagination,
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'none',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableSelectAll: false,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableSorting: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      columnOrder: [
        'invoiceNumber',
        'invoiceDate',
        'invoiceAmount',
        'reasonOfRejection',
        'milestone',
        'approvalStatus',
        'currentApprover',
        'city',
        'region',
        'vendor',
        'actions',
      ],
    },
    manualPagination: false,
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableBodyRowProps: ({ row }) => {
      const highlight = isPendingRejectionRow(row?.original);
      return {
        sx: {
          height: 52,
          minHeight: 52,
          maxHeight: 52,
          ...(highlight
            ? {
                backgroundColor: 'rgba(245, 158, 11, 0.10)',
                '&:hover': { backgroundColor: 'rgba(245, 158, 11, 0.16)' },
              }
            : {}),
        },
      };
    },
    muiTableBodyCellProps: ({ row }) => {
      const highlight = isPendingRejectionRow(row?.original);
      return {
        sx: {
          color: '#667085',
          backgroundColor: highlight ? 'rgba(245, 158, 11, 0.10)' : '#fff',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
          padding: '0 12px',
          ...(highlight ? { '&:hover': { backgroundColor: 'rgba(245, 158, 11, 0.16)' } } : {}),
          '&:first-of-type': {
            paddingLeft: '12px !important',
          },
        },
      };
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '56px',
        padding: '0 12px',
        alignItems: 'center',
        '&:first-of-type': {
          paddingLeft: '18px !important',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: 56,
        '& .MuiTableCell-root': {
          padding: '6px 12px',
          verticalAlign: 'middle',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          width: '100%',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left side - Search field */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: { xs: '100%', md: '300px' },
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right side - Filter dropdowns and Download button */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            flexWrap: 'wrap',
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {/* Invoice Date From/To (same style as All Invoices tab) */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Invoice date from"
              value={invoiceDateFrom ? dayjs(invoiceDateFrom) : null}
              onChange={(v) => setInvoiceDateFrom(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'DD-MM-YYYY',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: { xs: 140, sm: 168 },
                    flex: '1 1 160px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
            <DatePicker
              label="Invoice date to"
              value={invoiceDateTo ? dayjs(invoiceDateTo) : null}
              onChange={(v) => setInvoiceDateTo(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'DD-MM-YYYY',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: { xs: 140, sm: 168 },
                    flex: '1 1 160px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {(invoiceDateFrom || invoiceDateTo) && (
            <IconButton
              size="small"
              onClick={clearInvoiceDates}
              sx={{
                padding: '4px',
                color: '#757575',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                border: '1px solid #ccc',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  color: '#333',
                  border: '1px solid #999',
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}

          {/* Assignee Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 130 }, flex: '1 1 130px' }}>
            <Select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '6px 10px',
                  fontSize: '12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                ASSIGNEE
              </MenuItem>
              {assignees.map((assignee, index) => (
                <MenuItem
                  key={assignee}
                  value={assignee}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== assignees.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {assignee}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vendor Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 130 }, flex: '1 1 130px' }}>
            <Select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '6px 10px',
                  fontSize: '12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                VENDOR
              </MenuItem>
              {vendors.map((vendor, index) => (
                <MenuItem
                  key={vendor}
                  value={vendor}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Region Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 130 }, flex: '1 1 130px' }}>
            <Select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '6px 10px',
                  fontSize: '12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                REGION
              </MenuItem>
              {regions.map((region, index) => (
                <MenuItem
                  key={region}
                  value={region}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== regions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 130 }, flex: '1 1 130px' }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '6px 10px',
                  fontSize: '12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                STATUS
              </MenuItem>
              {statuses.map((status, index) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== statuses.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Download button */}
          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              minWidth: 'auto',
              padding: '8px',
              flex: { xs: '1 1 120px', md: '0 0 auto' },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <MaterialReactTable table={table} />

      <Dialog
        open={rejectDialog.open}
        onClose={() => {
          if (!rejectLoading) {
            setRejectDialog({ open: false, invoiceId: null, invoiceNumber: '', reason: '' });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>Reject Invoice</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please provide the reason of rejection. This will be emailed to the vendor.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason of rejection"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
            disabled={rejectLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setRejectDialog({ open: false, invoiceId: null, invoiceNumber: '', reason: '' })
            }
            disabled={rejectLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const invId = rejectDialog.invoiceId;
              const reason = (rejectDialog.reason || '').trim();
              if (!invId) {
                notify('error', 'Invoice id not found');
                return;
              }
              if (!reason) {
                notify('error', 'Reason of rejection is required');
                return;
              }
              setRejectLoading(true);
              try {
                const resp = await rejectInvoice(invId, reason, '');
                notify('success', resp?.message || 'Invoice rejected successfully');
                const tab = (fromTab || 'action-required').toString().trim();
                const suffix =
                  tab && tab !== 'action-required' ? `?tab=${encodeURIComponent(tab)}` : '';
                navigate(`/poWorkflowManagement${suffix}`);
              } finally {
                setRejectLoading(false);
              }
            }}
            disabled={rejectLoading}
            startIcon={rejectLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
